const SMALL_WIDTH = 150;
const MEDIUM_WIDTH = 200;
const MAX_WIDTH = 300;

export const PLANT_COMMON_COLUMNS_HEADERS = [
  "plant_type",
  "full_name",
  "family_name",
];

// Fields that are export-only (not shown in table, but included in CSV exports)
export const CSV_EXPORT_ONLY_FIELDS = [
  "genus",
  "species",
  "variety",
];

export const ENTERPRISE_COLUMNS_HEADERS = [
  "usace_wetland_status",
  "bioregional_distributions",
  "counties",
];

export const PLANT_COMMON_COLUMNS = [
  {
    title: "Full Scientific Name",
    dataIndex: "full_name",
    key: "full_name",
    className: "centered italic",
    sorter: true,
    show: true,
    width: MEDIUM_WIDTH,
    defaultSortOrder: 'ascend',
  },
  {
    title: "Family",
    headerClassName: "family-header",
    dataIndex: "family_name",
    key: "family_name",
    className: "centered capitalize",
    sorter: true,
    width: MEDIUM_WIDTH,
    show: true,
  },
  {
    title: "Plant Type",
    dataIndex: "plant_type",
    key: "plant_type",
    className: "centered",
    sorter: true,
    show: false,
  },
  // Export-only fields (not shown in table, but included in CSV exports)
  {
    title: "Genus",
    headerClassName: "genus-header",
    dataIndex: "genus",
    key: "genus",
    className: "centered capitalize",
    width: 50,
    sorter: true,
    show: false,
    exportOnly: true,
  },
  {
    title: "Species",
    dataIndex: "species",
    key: "species",
    className: "centered",
    width: 50,
    sorter: true,
    show: false,
    exportOnly: true,
  },
  {
    title: "Variety/Subspecies",
    dataIndex: "variety",
    key: "variety",
    className: "centered",
    width: MEDIUM_WIDTH,
    sorter: true,
    show: false,
    exportOnly: true,
  },
];

export const EJEPSON_COLUMNS = [
  {
    title: "Imported name from user",
    dataIndex: "database_original_name",
    key: "database_original_name",
    className: "centered",
    sorter: true,
    show: true,
  },
  {
    title: "Common Names",
    dataIndex: "common_names",
    key: "common_names",
    sorter: true,
    show: true,
  },
  {
    title: "Synonyms",
    dataIndex: "synonyms",
    key: "synonyms",
    className: "centered",
    sorter: true,
    show: true,
  },
  {
    title: "California Rare Plant Rank",
    dataIndex: "california_rare_plant_rank",
    key: "california_rare_plant_rank",
    sorter: true,
    show: true,
  },
  {
    title: "State Listing Status",
    dataIndex: "state_listing_status",
    key: "state_listing_status",
    className: "centered",
    sorter: true,
    show: true,
  },
  {
    title: "Federal Listing Status",
    dataIndex: "federal_listing_status",
    key: "federal_listing_status",
    className: "centered",
    sorter: true,
    show: true,
  },
  {
    title: "Bioregional Distributions",
    dataIndex: "bioregional_distributions",
    key: "bioregional_distributions",
    sorter: true,
    show: false,
  },
  {
    title: "Distribution Outside Of California",
    dataIndex: "distribution_outside_of_california",
    key: "distribution_outside_of_california",
    sorter: true,
    show: false,
  },
  {
    title: "Native",
    dataIndex: "native",
    key: "native",
    sorter: true,
    show: true,
    className: "centered",
  },
  {
    title: "IPC Listed Weed",
    dataIndex: "ipc_listed_weed",
    key: "ipc_listed_weed",
    sorter: true,
    show: false,
    className: "centered",
    render: (record) => (record ? "Yes" : "No"),
  },
  {
    title: "USACE Wetland Status",
    dataIndex: "usace_wetland_status",
    key: "usace_wetland_status",
    sorter: true,
    show: false,
    render: (record) => (record ? record.toUpperCase() : ""),
  },
  {
    title: "USACE Wetland Name",
    dataIndex: "usace_wetland_name",
    key: "usace_wetland_name",
    sorter: true,
    show: false,
    render: (record) =>
      record ? record.charAt(0).toUpperCase() + record.slice(1) : "",
  },
  {
    title: "USDA Common Name",
    dataIndex: "usda_common_name",
    key: "usda_common_name",
    sorter: true,
    show: false,
    render: (record) =>
      record ? record.charAt(0).toUpperCase() + record.slice(1) : "",
  },
  {
    title: "USDA Plant Symbol",
    dataIndex: "usda_symbol",
    key: "usda_symbol",
    sorter: true,
    show: false,
    render: (record) => (record ? record.toUpperCase() : ""),
  },
  {
    title: "USDA Arid West Status",
    dataIndex: "usda_arid_west_status",
    key: "usda_arid_west_status",
    sorter: true,
    show: false,
    render: (record) => (record ? record.toUpperCase() : ""),
  },
  {
    title: "USDA WMVC Status",
    dataIndex: "usda_wmvc_status",
    key: "usda_wmvc_status",
    sorter: true,
    show: false,
    render: (record) => (record ? record.toUpperCase() : ""),
  },
  {
    title: "CNPS Name",
    dataIndex: "cnps_name",
    key: "cnps_name",
    sorter: true,
    show: false,
    render: (record) =>
      record ? record.charAt(0).toUpperCase() + record.slice(1) : "",
  },
  {
    title: "CNPS Notes",
    dataIndex: "cnps_notes",
    key: "cnps_notes",
    sorter: true,
    show: false,
  },
  {
    title: "CNPS Taxonomy",
    dataIndex: "cnps_taxonomy",
    key: "cnps_taxonomy",
    sorter: true,
    show: false,
  },
  {
    title: "CNPS Threats",
    dataIndex: "cnps_threats",
    key: "cnps_threats",
    sorter: true,
    show: false,
  },
  {
    title: "CNPS Rare Plant Link",
    dataIndex: "CNPSRareLink",
    key: "CNPSRareLink",
    sorter: true,
    show: false,
    render: (record) => record ? <a href={record} target="_blank" rel="noopener noreferrer">View</a> : "",
  },
  {
    title: "Cal IPC Rating",
    dataIndex: "calipc_rating",
    key: "calipc_rating",
    sorter: true,
    show: false,
  },
  {
    title: "Cal IPC Description",
    dataIndex: "calipc_description",
    key: "calipc_description",
    sorter: true,
    show: false,
  },
  {
    title: "Cal IPC Name",
    dataIndex: "calipc_name",
    key: "calipc_name",
    sorter: true,
    show: false,
    render: (record) =>
      record ? record.charAt(0).toUpperCase() + record.slice(1) : "",
  },
  {
    title: "Cal IPC Genus",
    dataIndex: "calipc_genus",
    key: "calipc_genus",
    sorter: true,
    show: false,
    render: (record) =>
      record ? record.charAt(0).toUpperCase() + record.slice(1) : "",
  },
  {
    title: "Cal IPC Species",
    dataIndex: "calipc_species",
    key: "calipc_species",
    sorter: true,
    show: false,
  },
  {
    title: "Cal IPC Variety",
    dataIndex: "calipc_variety",
    key: "calipc_variety",
    sorter: true,
    show: false,
  },
  {
    title: "CDFA Rating",
    dataIndex: "cdfa_rating",
    key: "cdfa_rating",
    sorter: true,
    show: false,
  },
  {
    title: "CDFW Genus",
    dataIndex: "cdfw_genus",
    key: "cdfw_genus",
    sorter: true,
    show: false,
    render: (record) =>
      record ? record.charAt(0).toUpperCase() + record.slice(1) : "",
  },
  {
    title: "CDFW Species",
    dataIndex: "cdfw_species",
    key: "cdfw_species",
    sorter: true,
    show: false,
  },
  {
    title: "CDFW Variety",
    dataIndex: "cdfw_variety",
    key: "cdfw_variety",
    sorter: true,
    show: false,
  },
  {
    title: "CDFW Element Code",
    dataIndex: "cdfw_element_code",
    key: "cdfw_element_code",
    sorter: true,
    show: false,
    render: (record) => (record ? record.toUpperCase() : ""),
  },
  {
    title: "BLM Recognized Name",
    dataIndex: "blm_recognized_name",
    key: "blm_recognized_name",
    sorter: true,
    show: false,
    render: (record) =>
      record ? record.charAt(0).toUpperCase() + record.slice(1) : "",
  },
  {
    title: "BLM Known to Occur",
    dataIndex: "known_to_occur",
    key: "known_to_occur",
    sorter: true,
    show: false,
    render: (record) => (record ? "Yes" : "No"),
  },
  {
    title: "BLM Suspected to Occur",
    dataIndex: "suspected_to_occur",
    key: "suspected_to_occur",
    sorter: true,
    show: false,
    render: (record) => (record ? "Yes" : "No"),
  },
  {
    title: "BLM NNPS",
    dataIndex: "nnps",
    key: "nnps",
    sorter: true,
    show: false,
  },
  {
    title: "BLM NV State Rank",
    dataIndex: "nvstate_rank",
    key: "nvstate_rank",
    sorter: true,
    show: false,
  },
  {
    title: "BLM Family",
    dataIndex: "blm_family",
    key: "blm_family",
    sorter: true,
    show: false,
    render: (record) =>
      record ? record.charAt(0).toUpperCase() + record.slice(1) : "",
  },
  {
    title: "BLM Sensitive",
    dataIndex: "blm_sensitive",
    key: "blm_sensitive",
    sorter: true,
    show: false,
    render: (record) => (record ? "Yes" : "No"),
  },
  {
    title: "Forest Service Status",
    dataIndex: "forest_service_status",
    key: "forest_service_status",
    sorter: true,
    show: false,
  },
  {
    title: "BLM Rank",
    dataIndex: "blm_rank",
    key: "blm_rank",
    sorter: true,
    show: false,
  },
  {
    title: "AI Description",
    dataIndex: "ai_description",
    key: "ai_description",
    sorter: true,
    show: false,
  },
  {
    title: "Species Differentiators",
    dataIndex: "ai_differentiators",
    key: "ai_differentiators",
    sorter: true,
    show: false,
  },
  {
    title: "Species Differentiator",
    dataIndex: "col1_species_differentiator",
    key: "col1_species_differentiator",
    sorter: true,
    show: false,
    render: (text: string, record: any) => {
      if (!text && !record.col1_key_characters) return null;
      return (
        <div>
          {text && <p style={{ marginBottom: 4 }}>{text}</p>}
          {record.col1_key_characters && (
            <p style={{ fontSize: '0.85em', color: '#666', fontStyle: 'italic' }}>
              Key characters: {record.col1_key_characters}
            </p>
          )}
        </div>
      );
    },
  },
  {
    title: "Genus & Family Quick Tells",
    dataIndex: "col2_quick_tells",
    key: "col2_quick_tells",
    sorter: true,
    show: false,
    render: (text: string, record: any) => {
      const genusParts = text ? text.split('; ').filter(Boolean) : [];
      return (
        <div>
          {record.col2_genus_confidence_check && (
            <p style={{ marginBottom: 6, fontWeight: 500 }}>
              {record.col2_genus_confidence_check}
            </p>
          )}
          {genusParts.length > 0 && (
            <ul style={{ paddingLeft: 16, marginBottom: 8 }}>
              {genusParts.slice(0, 5).map((tell: string, i: number) => (
                <li key={i} style={{ fontSize: '0.85em', marginBottom: 2 }}>{tell}</li>
              ))}
              {genusParts.length > 5 && (
                <li style={{ fontSize: '0.85em', color: '#999' }}>
                  + {genusParts.length - 5} more...
                </li>
              )}
            </ul>
          )}
          {record.family_differentiator && (
            <div style={{ borderTop: '1px solid #eee', paddingTop: 6, marginTop: 4 }}>
              <p style={{ fontSize: '0.85em', fontWeight: 500, color: '#555' }}>
                In the {record.family || 'family'}:
              </p>
              <p style={{ fontSize: '0.85em' }}>{record.family_differentiator}</p>
              {record.family_key_characters && (
                <p style={{ fontSize: '0.8em', color: '#666', fontStyle: 'italic' }}>
                  Key: {record.family_key_characters}
                </p>
              )}
            </div>
          )}
        </div>
      );
    },
  },
  {
    title: "Custom Elevation",
    dataIndex: "chez_elevation",
    key: "chez_elevation",
    sorter: true,
    show: false,
  },
  {
    title: "Custom Bloom Window",
    dataIndex: "chez_bloom_window",
    key: "chez_bloom_window",
    sorter: true,
    show: false,
  },
  {
    title: "Custom Range",
    dataIndex: "chez_range",
    key: "chez_range",
    sorter: true,
    show: false,
  },
  {
    title: "Custom Plant Type",
    dataIndex: "chez_plant_type",
    key: "chez_plant_type",
    sorter: true,
    show: false,
  },
  {
    title: "Taxonomic Group",
    dataIndex: "monocot_dicot",
    key: "monocot_dicot",
    sorter: true,
    show: false,
  },
  {
    title: "Taxonomy Notes",
    dataIndex: "taxonomy_notes",
    key: "taxonomy_notes",
    sorter: true,
    show: false,
  },
  {
    title: "Habitat",
    dataIndex: "habitat",
    key: "habitat",
    sorter: true,
    show: false,
  },
  {
    title: "Elevation Range",
    dataIndex: "elevation_range",
    key: "elevation_range",
    sorter: true,
    show: false,
  },
  {
    title: "Flowering Time",
    dataIndex: "flowering_time",
    key: "flowering_time",
    className: "centered",
    sorter: true,
    show: false,
  },
  {
    title: "Local/County Rank",
    dataIndex: "local_county_rank",
    key: "local_county_rank",
    sorter: true,
    show: false,
  },
  {
    title: "Link to eFlora Jepson",
    dataIndex: "link_to_eflora_jepson",
    key: "link_to_eflora_jepson",
    sorter: true,
    show: false,
  },
  {
    title: "Cal Photo Link",
    dataIndex: "cal_photo_link",
    key: "cal_photo_link",
    sorter: true,
    show: false,
  },
  {
    title: "Global Rank",
    dataIndex: "global_rank",
    key: "global_rank",
    sorter: true,
    show: false,
  },
  {
    title: "State Rank",
    dataIndex: "state_rank",
    key: "state_rank",
    sorter: true,
    show: false,
  },
  {
    title: "Jepson Eflora And Other Notes",
    dataIndex: "jepson_eflora_other_notes",
    key: "jepson_eflora_other_notes",
    sorter: true,
    show: false,
  },
  {
    title: "Counties",
    dataIndex: "counties",
    key: "counties",
    sorter: true,
    show: false,
    render: (record) =>
      record && record.length > 0
        ? record
            .join("\n")
            .split("\n")
            .map((item, index) => {
              return index < record.join("\n").split("\n").length - 1 ? (
                <span>{item.trim()}, </span>
              ) : (
                <span>{item.trim()}</span>
              );
            })
        : null,
  },
  {
    title: "CNPS Quads",
    dataIndex: "cnps_quads",
    key: "cnps_quads",
    sorter: true,
    show: false,
  },
  {
    title: "CNPS California Counties and Islands",
    dataIndex: "cnps_california_counties_and_islands",
    key: "cnps_california_counties_and_islands",
    sorter: true,
    show: false,
  },
  // {
  //   title: "Note",
  //   dataIndex: "note",
  //   key: "note",
  //   className: "centered",
  //   // width: MAX_WIDTH,
  //   sorter: true,
  //   show: false,
  //   render: (record) => {
  //     return (
  //       <div>
  //         <textarea
  //           rows={5}
  //           cols={40}
  //           className="item-note"
  //           value={record.original.note}
  //           data-item-id={record.original.item_id}
  //           // onChange={onNoteChange}
  //         ></textarea>
  //         <button
  //           className="update-note"
  //           data-item-id={record.original.item_id}
  //           // onClick={onUpdateNote}
  //         >
  //           Update
  //         </button>
  //       </div>
  //     );
  //   },
  // },
];
// Export-only fields - included in CSV but not shown in table
export const NameInnerColumnKeys = ["family", "genus", "species", "variety"];

// Deprecated: Old Name column with children - replaced with flat Full Scientific Name column
export const NameColumn = {
  title: "Full Scientific Name",
  dataIndex: "full_name",
  key: "full_name",
  className: "centered italic",
  sorter: true,
  width: MEDIUM_WIDTH,
  ellipsis: true,
  show: true,
};

// Export-only columns (for CSV export, not displayed in table)
export const ExportOnlyColumns = [
  {
    title: "Genus",
    headerClassName: "genus-header",
    dataIndex: "genus",
    key: "genus",
    className: "centered capitalize",
    width: 100,
    sorter: true,
    ellipsis: true,
    show: false,
    exportOnly: true,
  },
  {
    title: "Species",
    dataIndex: "species",
    key: "species",
    className: "centered",
    width: 100,
    sorter: true,
    ellipsis: true,
    show: false,
    exportOnly: true,
  },
  {
    title: "Variety/Subspecies",
    dataIndex: "variety",
    key: "variety",
    className: "centered",
    width: MEDIUM_WIDTH,
    sorter: true,
    ellipsis: true,
    show: false,
    exportOnly: true,
  },
];

export const HiddenColumnKeys = ["cal_photo_link", "bioregional_distributions", "distribution_outside_of_california", "url", "jepson_description", "jepson_eflora_other_notes"]